import React, { useState, useEffect } from 'react';
import { Lock, Users, DollarSign, BarChart3, Mail, Phone, Calendar, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [poolAnalyses, setPoolAnalyses] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        toast({ title: "Success", description: "Logged in successfully" });
        fetchData();
      } else {
        toast({ 
          title: "Error", 
          description: "Invalid password", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Login failed", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statsRes, contactsRes, analysesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/contacts`, { headers }),
        fetch(`${API_URL}/api/admin/pool-analyses`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (analysesRes.ok) setPoolAnalyses(await analysesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/contacts/${contactId}/status?status=${newStatus}`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast({ title: "Success", description: "Status updated" });
        fetchData();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const updateAnalysisStatus = async (analysisId, newStatus) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/pool-analyses/${analysisId}/status?status=${newStatus}`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast({ title: "Success", description: "Status updated" });
        fetchData();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-center">Admin Dashboard</CardTitle>
            <p className="text-center text-slate-600">Waterlife Pools & Spas</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-center"
              />
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600">Waterlife Pools & Spas - Lead Management</p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm">Total Contacts</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_contacts}</p>
                  </div>
                  <Users className="w-10 h-10 text-cyan-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm">Pool Analyses</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_pool_analyses}</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm">New Leads</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {stats.new_contacts + stats.new_pool_analyses}
                    </p>
                  </div>
                  <Mail className="w-10 h-10 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-sm">Revenue Potential</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${(stats.total_revenue_potential || 0).toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList>
            <TabsTrigger value="contacts">
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="pool-analyses">
              Pool Analyses ({poolAnalyses.length})
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <CardTitle>Contact Form Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div 
                      key={contact.id}
                      className="border rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelectedContact(contact)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-lg">{contact.name}</h3>
                          <p className="text-slate-600 text-sm">{contact.email} • {contact.phone}</p>
                        </div>
                        <select
                          value={contact.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateContactStatus(contact.id, e.target.value);
                          }}
                          className="border rounded px-3 py-1 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <p className="text-sm mb-2"><strong>Service:</strong> {contact.service}</p>
                      <p className="text-slate-700 text-sm line-clamp-2">{contact.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(contact.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pool Analyses Tab */}
          <TabsContent value="pool-analyses">
            <Card>
              <CardHeader>
                <CardTitle>Pool Analysis Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {poolAnalyses.map((analysis) => (
                    <div 
                      key={analysis.id}
                      className="border rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelectedAnalysis(analysis)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-lg">{analysis.name}</h3>
                          <p className="text-slate-600 text-sm">{analysis.email} • {analysis.phone}</p>
                        </div>
                        <select
                          value={analysis.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateAnalysisStatus(analysis.id, e.target.value);
                          }}
                          className="border rounded px-3 py-1 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="new">New</option>
                          <option value="quoted">Quoted</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-2">
                        <p className="text-sm"><strong>Pool:</strong> {analysis.pool_type} ({analysis.pool_size})</p>
                        <p className="text-sm"><strong>Features:</strong> {analysis.features.length}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-3 mt-2">
                        <p className="text-green-900 font-bold text-lg">
                          Estimate: ${analysis.estimate.total.toLocaleString()}
                        </p>
                        <p className="text-green-700 text-sm">
                          Monthly: ${analysis.estimate.monthly_payment}/mo
                        </p>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(analysis.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact Detail Modal */}
        {selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle>Contact Details</CardTitle>
                  <button onClick={() => setSelectedContact(null)}>
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="font-bold">Name:</label>
                    <p>{selectedContact.name}</p>
                  </div>
                  <div>
                    <label className="font-bold">Email:</label>
                    <p><a href={`mailto:${selectedContact.email}`} className="text-cyan-600">{selectedContact.email}</a></p>
                  </div>
                  <div>
                    <label className="font-bold">Phone:</label>
                    <p><a href={`tel:${selectedContact.phone}`} className="text-cyan-600">{selectedContact.phone}</a></p>
                  </div>
                  <div>
                    <label className="font-bold">Service:</label>
                    <p className="capitalize">{selectedContact.service}</p>
                  </div>
                  <div>
                    <label className="font-bold">Message:</label>
                    <p className="bg-slate-50 p-4 rounded">{selectedContact.message}</p>
                  </div>
                  <div>
                    <label className="font-bold">Submitted:</label>
                    <p>{new Date(selectedContact.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pool Analysis Detail Modal */}
        {selectedAnalysis && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle>Pool Analysis Details</CardTitle>
                  <button onClick={() => setSelectedAnalysis(null)}>
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="font-bold">Name:</label>
                    <p>{selectedAnalysis.name}</p>
                  </div>
                  <div>
                    <label className="font-bold">Email:</label>
                    <p><a href={`mailto:${selectedAnalysis.email}`} className="text-cyan-600">{selectedAnalysis.email}</a></p>
                  </div>
                  <div>
                    <label className="font-bold">Phone:</label>
                    <p><a href={`tel:${selectedAnalysis.phone}`} className="text-cyan-600">{selectedAnalysis.phone}</a></p>
                  </div>
                  <div>
                    <label className="font-bold">Address:</label>
                    <p>{selectedAnalysis.address || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="font-bold">Timeline:</label>
                    <p className="capitalize">{selectedAnalysis.timeline || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="font-bold">Pool Configuration:</label>
                    <p>Type: {selectedAnalysis.pool_type} | Size: {selectedAnalysis.pool_size}</p>
                    <p>Features: {selectedAnalysis.features.join(', ') || 'None'}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded">
                    <label className="font-bold">Cost Estimate:</label>
                    <div className="mt-2 space-y-1">
                      <p>Base: ${selectedAnalysis.estimate.base_price.toLocaleString()}</p>
                      <p>Features: ${selectedAnalysis.estimate.features.toLocaleString()}</p>
                      <p>Labor: ${selectedAnalysis.estimate.labor.toLocaleString()}</p>
                      <p>Permits: ${selectedAnalysis.estimate.permits.toLocaleString()}</p>
                      <hr className="my-2" />
                      <p className="font-bold text-lg">Total: ${selectedAnalysis.estimate.total.toLocaleString()}</p>
                      <p className="text-sm">Monthly: ${selectedAnalysis.estimate.monthly_payment}/mo</p>
                    </div>
                  </div>
                  <div>
                    <label className="font-bold">Photos:</label>
                    <p>{selectedAnalysis.photo_urls.length} uploaded</p>
                  </div>
                  <div>
                    <label className="font-bold">Submitted:</label>
                    <p>{new Date(selectedAnalysis.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
